import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";
import { Wordmark } from "@/components/site/Layout";
import AdminDashboard from "@/pages/AdminDashboard";

export const ADMIN_API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const TOKEN_KEY = "ostelo_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const adminApi = () =>
  axios.create({ baseURL: ADMIN_API, headers: { Authorization: `Bearer ${getToken() || ""}` } });

export default function Admin() {
  const [status, setStatus] = useState("checking"); // checking | login | ready

  useEffect(() => {
    document.title = "Back-office — Ostelo";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      if (meta.parentNode) meta.parentNode.removeChild(meta);
      document.title = "Ostelo";
    };
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setStatus("login");
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setStatus("login");
      return;
    }
    adminApi()
      .get("/content")
      .then(() => setStatus("ready"))
      .catch(() => {
        setToken(null);
        setStatus("login");
      });
  }, []);

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ostelo-ivory">
        <Loader2 className="animate-spin text-ostelo-blue" size={28} />
      </div>
    );
  }
  if (status === "login") return <AdminLogin onSuccess={() => setStatus("ready")} />;
  return <AdminDashboard onLogout={handleLogout} />;
}

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${ADMIN_API}/login`, { email, password });
      setToken(data.token);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ostelo-ivory px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border border-ostelo-deep/10 bg-white p-8 shadow-[0_20px_60px_rgb(12,27,71,0.08)]"
        data-testid="admin-login"
      >
        <div className="flex justify-center">
          <Wordmark />
        </div>
        <h1 className="mt-6 text-center font-display text-xl font-extrabold text-ostelo-deep">Back-office</h1>
        <p className="mt-1 text-center text-sm text-ostelo-ink/55">Connectez-vous pour éditer le contenu du site.</p>
        <label className="mt-7 block">
          <span className="text-xs font-semibold text-ostelo-deep">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-ostelo-deep/15 px-4 py-3 text-sm outline-none focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15"
            data-testid="admin-email-input"
            autoComplete="username"
          />
        </label>
        <label className="mt-4 block">
          <span className="text-xs font-semibold text-ostelo-deep">Mot de passe</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-ostelo-deep/15 px-4 py-3 text-sm outline-none focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15"
            data-testid="admin-password-input"
            autoComplete="current-password"
          />
        </label>
        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-ostelo-deep py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-ostelo-blue disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
          Se connecter
        </button>
      </form>
    </div>
  );
}
